import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";

const FOUNDER_IMG = "https://customer-assets.emergentagent.com/job_vital-courier-pro/artifacts/pwdzptqp_image.png";
const CFO_IMG = "https://customer-assets.emergentagent.com/job_vital-courier-pro/artifacts/6hresfv0_image.png";

const LEADERS = [
  {
    testid: "ceo",
    role: "Founder & CEO",
    name: "Soulat Testing LLC",
    img: FOUNDER_IMG,
    bio: "Leading BioFleet's mission to deliver medical logistics and diagnostics with precision, discretion, and unwavering care.",
  },
  {
    testid: "cfo",
    role: "CFO & Head of Logistics",
    name: "CFO",
    img: CFO_IMG,
    bio: "Steering financial strategy and running the logistics department — ensuring every route, dispatch, and delivery scales responsibly while protecting every client relationship.",
  },
];

export const About = () => (
  <section id="about" className="py-24 md:py-40 bg-secondary/40 border-y border-border">
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
      <motion.div
        initial={{ opacity: 0, x: -40 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="lg:col-span-5"
      >
        <div className="relative">
          <div className="absolute -top-4 -left-4 w-full h-full border border-primary -z-0" />
          <div className="relative border border-border overflow-hidden clip-frame bg-card">
            <img
              data-testid="founder-image"
              src={FOUNDER_IMG}
              alt="Founder, BioFleet Couriers & Soulat Testing LLC"
              className="w-full h-full object-cover object-top grayscale hover:grayscale-0 transition-all duration-700"
            />
          </div>
          <div className="absolute bottom-6 left-6 bg-background border border-border px-4 py-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Founder & CEO</div>
            <div className="font-display font-bold text-sm tracking-tight">Soulat Testing LLC</div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="lg:col-span-7"
      >
        <span className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground">/ About Us</span>
        <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter uppercase mt-5 mb-8 leading-[0.95]">
          Built on Trust,<br /><span className="text-primary">Driven</span> by Care.
        </h2>
        <div className="space-y-6 max-w-2xl">
          <p className="text-base md:text-lg font-mono text-muted-foreground leading-relaxed">
            BioFleet Couriers &amp; Soulat Testing LLC was founded on a simple belief — that medical logistics and diagnostic services should be handled with the same precision, discretion, and dignity as the people they serve.
          </p>
          <p className="text-base md:text-lg font-mono text-muted-foreground leading-relaxed">
            From secure specimen transport to accredited DNA and drug testing and mobile notary services, our team delivers reliability you can measure and care you can feel — every route, every result, every time.
          </p>
        </div>
        <div className="mt-10 flex flex-wrap gap-4">
          {["Licensed & Insured", "HIPAA-Conscious", "Chain-of-Custody"].map((b) => (
            <div key={b} className="flex items-center gap-2 border border-border px-4 py-3">
              <ShieldCheck size={16} className="text-accent" />
              <span className="text-xs font-mono uppercase tracking-[0.15em]">{b}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>

    {/* Leadership */}
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 mt-24 md:mt-36">
      <div className="flex items-end justify-between mb-12 md:mb-16 border-t border-border pt-10">
        <h3 className="font-display font-black text-3xl md:text-5xl tracking-tighter uppercase leading-none">
          Leadership
        </h3>
        <span className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground hidden md:block">/ The Team</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
        {LEADERS.map((l, i) => (
          <motion.div
            key={l.role}
            data-testid={`leader-${l.testid}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className="group border border-border bg-card overflow-hidden"
          >
            <div className="relative aspect-[4/5] md:aspect-[3/2] overflow-hidden border-b border-border">
              <img
                src={l.img}
                alt={`${l.role}, BioFleet Couriers & Soulat Testing LLC`}
                className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-all duration-700"
              />
              <div className="absolute top-4 left-4 bg-background border border-border px-3 py-1.5">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary">{l.role}</span>
              </div>
            </div>
            <div className="p-7 md:p-9">
              <div className="font-display font-bold text-xl md:text-2xl tracking-tight">{l.name}</div>
              <p className="text-sm font-mono text-muted-foreground leading-relaxed mt-3">{l.bio}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
